import { modalName, standalonePresentationLinks } from '@va/constants';
import { getAgreement } from '@va/dashboard/actions/api';
import { openModal } from '@va/dashboard/actions/ui';
import { getRequestStatus, getAgreement as getSignedAgreement } from '@va/dashboard/selectors/api';
import { getWebsite, websiteRoleSelectors } from '@va/dashboard/selectors/core';
import { isMobileDevice } from '@va/dashboard/selectors/ui';
import { PageContent } from '@va/dashboard/shared/ui-layout';
import { getDownloadAgreementLink } from '@va/http-client';
import { ExportIcon, ListIcon, PencilEditIcon } from '@va/icons';
import { useLocalizationContext } from '@va/localization';
import { getAccessToken } from '@va/standalone/shared/helpers';
import { Button, Heading3, IconWithBackground, Paragraph, fontWeights, paragraphSizes } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { getLocalizedDate, someInProgress } from '@va/util/helpers';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { LegalDocumentsRowItem } from './LegalDocumentsRowItem';
import SignDpaModal from './SignDpaModal';

const LegalDocuments = () => {
  const { translate, locale } = useLocalizationContext();
  const [isSignDpaModalVisible, setSignDpaModalVisible] = useState(false);
  const dispatch = useDispatch();
  const isMobile = useSelector(isMobileDevice);
  const agreement = useSelector(getSignedAgreement);
  const websiteEmail = useSelector(getWebsite)?.email;
  const signDPAStatus = useSelector((state) => getRequestStatus(state, 'signDPA'));
  const getAgreementStatus = useSelector((state) => getRequestStatus(state, 'getAgreement'));
  const agreementTermsStatus = useSelector((state) => getRequestStatus(state, 'agreement'));
  const websiteOwner = useSelector(websiteRoleSelectors.isWebsiteOwner);

  const areDocumentsAvailable = useMemo(() => websiteOwner, [websiteOwner]);

  useEffect(() => {
    if (areDocumentsAvailable) dispatch(getAgreement());
  }, [areDocumentsAvailable, dispatch]);

  const agreementDownloadLink = useMemo(() => {
    return getDownloadAgreementLink(getAccessToken() ?? '', agreement.get('privacyTermsAcceptedFile'));
  }, [agreement]);

  const handleSignDpa = () => {
    websiteEmail ? setSignDpaModalVisible(true) : dispatch(openModal(modalName.accountSettingsRequired));
  };

  const isInProgress = useMemo(
    () => someInProgress(agreementTermsStatus, signDPAStatus, getAgreementStatus),
    [agreementTermsStatus, signDPAStatus, getAgreementStatus],
  );

  return (
    <>
      {isSignDpaModalVisible && (
        <SignDpaModal isModalOpen={isSignDpaModalVisible} onClose={() => setSignDpaModalVisible(false)} />
      )}
      <PageContent className='relative'>
        <div className='flex justify-center items-center'>
          <div
            className={classNames('flex flex-col sm:w-full w-600px mb-18px relative', {
              'opacity-30': !areDocumentsAvailable,
            })}
          >
            <Heading3 weight={fontWeights.semibold}>{translate('all.privacyCenter.legalDocuments.title')}</Heading3>
            <Paragraph className='mt-6px font-450' colorClassName='text-gray-charcoal'>
              {translate('all.privacyCenter.legalDocuments.subInfo')}
            </Paragraph>
            {isInProgress ? (
              <Skeleton count={2} className='h-72px rounded-18px first:mt-18px mt-3' />
            ) : (
              <>
                <LegalDocumentsRowItem
                  icon={<IconWithBackground className='shrink-0 bg-white' icon={() => <ListIcon color='#696969' />} />}
                  title={translate('label.termsOfUse')}
                  subTitle={
                    isMobile
                      ? undefined
                      : translate('label.lastUpdated', {
                          date: getLocalizedDate(agreement.get('signedAt'), locale),
                        })
                  }
                  className='mt-18px'
                  rightSection={
                    <Button
                      text={translate('button.viewFile')}
                      color='quinary'
                      onClick={() => window.open(standalonePresentationLinks.terms, '_blank')}
                    />
                  }
                />
                <LegalDocumentsRowItem
                  icon={
                    <IconWithBackground className='shrink-0 bg-white' icon={() => <PencilEditIcon color='#696969' />} />
                  }
                  title={translate('label.dataProtectionAgreement')}
                  className='mt-3'
                  rightSection={
                    <>
                      {!isMobile && agreement.get('agreedAt') && websiteOwner && areDocumentsAvailable && (
                        <Button text={translate('button.signFile')} onClick={handleSignDpa} />
                      )}
                      <Button
                        text={translate('button.viewFile')}
                        color='quinary'
                        onClick={() => window.open(translate('all.privacyCenter.legalDocuments.dpaLink'), '_blank')}
                      />
                      {!isMobile && agreement.get('privacyTermsAcceptedFile') && areDocumentsAvailable && (
                        <Button
                          icon={() => <ExportIcon color={'#3C3C3C'} />}
                          color='quinary'
                          tooltip={translate('all.privacyCenter.legalDocuments.downloadDpaTooltip')}
                          onClick={() => window.open(agreementDownloadLink, '_blank')}
                        />
                      )}
                    </>
                  }
                />
              </>
            )}
            <Paragraph
              className='mt-6px px-4'
              weight={fontWeights.medium}
              colorClassName='text-gray-devilSolid'
              size={paragraphSizes.tiny}
            >
              {translate('all.privacyCenter.legalDocuments.downloadAndSignNote')}
            </Paragraph>
            {!areDocumentsAvailable && (
              <TooltipWrapper
                content={translate('all.privacyCenter.legalDocuments.agencyOwnerOnlyTooltip')}
                followCursorAxis='both'
              >
                <div className='absolute left-0 right-0 bottom-0 top-0 cursor-not-allowed' />
              </TooltipWrapper>
            )}
          </div>
        </div>
      </PageContent>
    </>
  );
};

export default LegalDocuments;
